import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  Camera, 
  Upload, 
  TrendingUp, 
  TrendingDown, 
  Minus,
  Brain, 
  Sparkles, 
  Lock, 
  Crown, 
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
  Utensils,
  Activity,
  Clock,
  Target,
  Zap,
  ArrowRight,
  X,
  Image as ImageIcon
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AIFeatures = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  // Subscription state
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  
  // Meal Photo AI state
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [analyzingPhoto, setAnalyzingPhoto] = useState(false);
  const [photoResult, setPhotoResult] = useState(null);
  const [mealNotes, setMealNotes] = useState('');
  
  // Blood Sugar Predictor state
  const [predicting, setPredicting] = useState(false);
  const [prediction, setPrediction] = useState(null);
  
  // Upgrade dialog
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);

  useEffect(() => {
    fetchSubscriptionStatus();
  }, []);

  const fetchSubscriptionStatus = async () => {
    setLoadingStatus(true);
    try {
      const response = await axios.get(`${API_URL}/api/user/subscription-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Error fetching subscription status:', error);
      setSubscriptionStatus({ effective_plan: 'free', features: {} });
    }
    setLoadingStatus(false);
  };

  const handleImageSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
        toast({
          title: "Invalid Format",
          description: "Please upload a JPEG, PNG, or WebP image",
          variant: "destructive"
        });
        return;
      }
      
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: "File Too Large",
          description: "Please upload an image smaller than 10MB",
          variant: "destructive"
        });
        return;
      }
      
      setSelectedImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
      
      setPhotoResult(null);
    }
  };

  const handleAnalyzePhoto = async () => {
    if (!selectedImage) {
      toast({
        title: "No Image",
        description: "Please select an image first",
        variant: "destructive"
      });
      return;
    }
    
    setAnalyzingPhoto(true);
    try {
      // Convert image to base64
      const reader = new FileReader();
      reader.readAsDataURL(selectedImage);
      reader.onloadend = async () => {
        const base64 = reader.result.split(',')[1];
        
        try {
          const response = await axios.post(
            `${API_URL}/api/ai/analyze-meal-photo`,
            { 
              image_base64: base64,
              notes: mealNotes 
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          if (response.data.success) {
            setPhotoResult(response.data);
            toast({
              title: "Analysis Complete!",
              description: "Your meal has been analyzed"
            });
          } else if (response.data.error === 'premium_required') {
            setUpgradeDialogOpen(true);
          } else {
            throw new Error(response.data.message || 'Analysis failed');
          }
        } catch (error) {
          if (error.response?.data?.error === 'premium_required') {
            setUpgradeDialogOpen(true);
          } else {
            toast({
              title: "Analysis Failed",
              description: error.response?.data?.detail || error.message || "Please try again",
              variant: "destructive"
            });
          }
        }
        setAnalyzingPhoto(false);
      };
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to process image",
        variant: "destructive"
      });
      setAnalyzingPhoto(false);
    }
  };

  const handlePredictGlucose = async () => {
    setPredicting(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/ai/predict-glucose`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setPrediction(response.data);
        toast({
          title: "Prediction Generated!",
          description: "Check your glucose forecast below"
        });
      } else if (response.data.error === 'premium_required') {
        setUpgradeDialogOpen(true);
      } else if (response.data.error === 'insufficient_data') {
        toast({
          title: "Need More Data",
          description: response.data.message,
          variant: "destructive"
        });
      } else {
        throw new Error(response.data.message || 'Prediction failed');
      }
    } catch (error) {
      if (error.response?.data?.error === 'premium_required') {
        setUpgradeDialogOpen(true);
      } else {
        toast({
          title: "Prediction Failed",
          description: error.response?.data?.detail || error.message || "Please try again",
          variant: "destructive"
        });
      }
    }
    setPredicting(false);
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'rising': return <TrendingUp className="w-5 h-5 text-orange-500" />;
      case 'falling': return <TrendingDown className="w-5 h-5 text-blue-500" />;
      default: return <Minus className="w-5 h-5 text-green-500" />;
    }
  };

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'high': return 'bg-red-100 text-red-700 border-red-200';
      case 'elevated': return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'low': return 'bg-blue-100 text-blue-700 border-blue-200';
      default: return 'bg-green-100 text-green-700 border-green-200';
    }
  };

  const getGlycemicColor = (impact) => {
    switch (impact) {
      case 'high': return 'text-red-600 bg-red-50';
      case 'moderate': return 'text-orange-600 bg-orange-50';
      default: return 'text-green-600 bg-green-50';
    }
  };

  const hasAccess = subscriptionStatus?.features?.meal_photo_ai || 
                   subscriptionStatus?.is_trial_active ||
                   ['pro', 'enterprise'].includes(subscriptionStatus?.effective_plan);

  if (loadingStatus) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 rounded-2xl p-8 text-white shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
              <Sparkles className="w-7 h-7" />
            </div>
            <div>
              <h2 className="text-3xl font-bold">AI-Powered Features</h2>
              <p className="text-violet-100 text-lg">Meal Photo Analysis & Blood Sugar Predictions</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {subscriptionStatus?.is_trial_active && (
              <Badge className="bg-yellow-500 text-yellow-900 px-3 py-1">
                <Clock className="w-4 h-4 mr-1" />
                Trial: {subscriptionStatus.trial_days_remaining} days left
              </Badge>
            )}
            {hasAccess ? (
              <Badge className="bg-emerald-500 text-white px-3 py-1">
                <Crown className="w-4 h-4 mr-1" />
                Pro Access
              </Badge>
            ) : (
              <Button 
                onClick={() => navigate('/pricing')}
                className="bg-white text-violet-600 hover:bg-violet-50"
              >
                <Crown className="w-4 h-4 mr-2" />
                Upgrade to Pro
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Trial Banner */}
      {subscriptionStatus?.is_trial_active && (
        <Card className="border-2 border-yellow-400 bg-yellow-50 dark:bg-yellow-900/20">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Zap className="w-6 h-6 text-yellow-600" />
                <div>
                  <p className="font-semibold text-yellow-800 dark:text-yellow-200">
                    🎉 You are on a 7-Day Pro Trial!
                  </p>
                  <p className="text-sm text-yellow-700 dark:text-yellow-300">
                    {subscriptionStatus.trial_days_remaining} days remaining. Enjoy all Pro features including AI meal analysis and glucose predictions.
                  </p>
                </div>
              </div>
              <Button 
                variant="outline" 
                className="border-yellow-600 text-yellow-700 hover:bg-yellow-100"
                onClick={() => navigate('/pricing')}
              >
                Keep Pro Forever
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="meal-photo" className="space-y-6">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="meal-photo" className="flex items-center gap-2">
            <Camera className="w-4 h-4" />
            Meal Photo AI
          </TabsTrigger>
          <TabsTrigger value="predictor" className="flex items-center gap-2">
            <Activity className="w-4 h-4" />
            Glucose Predictor
          </TabsTrigger>
        </TabsList>

        {/* Meal Photo AI Tab */}
        <TabsContent value="meal-photo" className="space-y-6">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Upload Section */}
            <Card className="shadow-lg border-0">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Camera className="w-5 h-5 text-violet-500" />
                  Analyze Your Meal
                </CardTitle>
                <CardDescription>
                  Take a photo of your meal and our AI will identify the food and estimate nutrition
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Image Upload Area */}
                <div 
                  className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all
                    ${imagePreview ? 'border-violet-400 bg-violet-50' : 'border-gray-300 hover:border-violet-400 hover:bg-violet-50/50'}`}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {imagePreview ? (
                    <div className="relative">
                      <img 
                        src={imagePreview} 
                        alt="Selected meal" 
                        className="max-h-64 mx-auto rounded-lg shadow-md"
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute top-2 right-2 bg-white/80 hover:bg-white"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedImage(null);
                          setImagePreview(null);
                          setPhotoResult(null);
                        }}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="py-8">
                      <ImageIcon className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                      <p className="text-gray-600 font-medium">Click to upload a meal photo</p>
                      <p className="text-sm text-gray-500 mt-1">JPEG, PNG, or WebP • Max 10MB</p>
                    </div>
                  )}
                </div>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleImageSelect}
                  className="hidden"
                />

                {/* Optional Notes */}
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Additional notes (optional)
                  </label>
                  <Input
                    placeholder="e.g., homemade, large portion, with extra sauce..."
                    value={mealNotes}
                    onChange={(e) => setMealNotes(e.target.value)}
                    className="mt-1"
                  />
                </div>

                {/* Analyze Button */}
                <Button 
                  onClick={handleAnalyzePhoto}
                  disabled={!selectedImage || analyzingPhoto || !hasAccess}
                  className="w-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700"
                >
                  {analyzingPhoto ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Analyzing with AI...
                    </>
                  ) : !hasAccess ? (
                    <>
                      <Lock className="w-4 h-4 mr-2" />
                      Upgrade to Pro
                    </>
                  ) : (
                    <>
                      <Brain className="w-4 h-4 mr-2" />
                      Analyze Meal
                    </>
                  )}
                </Button>

                {!hasAccess && (
                  <p className="text-sm text-center text-gray-500">
                    <Lock className="w-3 h-3 inline mr-1" />
                    This is a Pro feature. <button onClick={() => navigate('/pricing')} className="text-violet-600 hover:underline">Upgrade now</button>
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Results Section */}
            <Card className="shadow-lg border-0">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Utensils className="w-5 h-5 text-emerald-500" />
                  Analysis Results
                </CardTitle>
              </CardHeader>
              <CardContent>
                {photoResult?.nutrition ? (
                  <div className="space-y-4">
                    {/* Food Items */}
                    <div>
                      <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">Identified Foods</h4>
                      <div className="flex flex-wrap gap-2">
                        {photoResult.nutrition.food_items?.map((item, i) => (
                          <Badge key={i} variant="outline" className="px-3 py-1">
                            {item}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    {/* Nutrition Grid */}
                    <div className="grid grid-cols-3 gap-3">
                      <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
                        <p className="text-2xl font-bold text-blue-700 dark:text-blue-400">
                          {photoResult.nutrition.total_calories || 0}
                        </p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Calories</p>
                      </div>
                      <div className="text-center p-3 bg-orange-50 dark:bg-orange-900/30 rounded-lg">
                        <p className="text-2xl font-bold text-orange-700 dark:text-orange-400">
                          {photoResult.nutrition.total_carbs || 0}g
                        </p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Carbs</p>
                      </div>
                      <div className="text-center p-3 bg-red-50 dark:bg-red-900/30 rounded-lg">
                        <p className="text-2xl font-bold text-red-700 dark:text-red-400">
                          {photoResult.nutrition.total_sugar || 0}g
                        </p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Sugar</p>
                      </div>
                      <div className="text-center p-3 bg-green-50 dark:bg-green-900/30 rounded-lg">
                        <p className="text-2xl font-bold text-green-700 dark:text-green-400">
                          {photoResult.nutrition.total_protein || 0}g
                        </p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Protein</p>
                      </div>
                      <div className="text-center p-3 bg-yellow-50 dark:bg-yellow-900/30 rounded-lg">
                        <p className="text-2xl font-bold text-yellow-700 dark:text-yellow-400">
                          {photoResult.nutrition.total_fat || 0}g
                        </p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Fat</p>
                      </div>
                      <div className="text-center p-3 bg-purple-50 dark:bg-purple-900/30 rounded-lg">
                        <p className="text-2xl font-bold text-purple-700 dark:text-purple-400">
                          {photoResult.nutrition.total_fiber || 0}g
                        </p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Fiber</p>
                      </div>
                    </div>

                    {/* Glycemic Impact */}
                    <div className={`p-4 rounded-lg ${getGlycemicColor(photoResult.nutrition.glycemic_impact)}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold">Glycemic Impact</p>
                          <p className="text-sm capitalize">{photoResult.nutrition.glycemic_impact || 'moderate'}</p>
                        </div>
                        {photoResult.nutrition.diabetes_friendly ? (
                          <CheckCircle2 className="w-8 h-8 text-green-600" />
                        ) : (
                          <AlertTriangle className="w-8 h-8 text-orange-500" />
                        )}
                      </div>
                    </div>

                    {/* Tips */}
                    {photoResult.nutrition.tips && (
                      <div className="p-4 bg-violet-50 dark:bg-violet-900/20 rounded-lg border border-violet-200">
                        <p className="font-semibold text-violet-800 dark:text-violet-300 mb-1">
                          💡 Diabetes Tip
                        </p>
                        <p className="text-sm text-violet-700 dark:text-violet-400">
                          {photoResult.nutrition.tips}
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <Camera className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p>Upload and analyze a meal photo to see nutritional information</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Blood Sugar Predictor Tab */}
        <TabsContent value="predictor" className="space-y-6">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Predict Section */}
            <Card className="shadow-lg border-0">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="w-5 h-5 text-blue-500" />
                  Blood Sugar Predictor
                </CardTitle>
                <CardDescription>
                  AI analyzes your recent glucose readings, meals, and activities to predict your blood sugar trend
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-blue-800 dark:text-blue-300 mb-2">How it works</h4>
                  <ul className="text-sm text-blue-700 dark:text-blue-400 space-y-1">
                    <li>• Analyzes your last 20 glucose readings</li>
                    <li>• Considers recent meals and carb intake</li>
                    <li>• Factors in physical activity</li>
                    <li>• Reviews medication schedule</li>
                  </ul>
                </div>

                <Button 
                  onClick={handlePredictGlucose}
                  disabled={predicting || !hasAccess}
                  className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700"
                >
                  {predicting ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Analyzing Your Data...
                    </>
                  ) : !hasAccess ? (
                    <>
                      <Lock className="w-4 h-4 mr-2" />
                      Upgrade to Pro
                    </>
                  ) : (
                    <>
                      <Brain className="w-4 h-4 mr-2" />
                      Generate Prediction
                    </>
                  )}
                </Button>

                {!hasAccess && (
                  <p className="text-sm text-center text-gray-500">
                    <Lock className="w-3 h-3 inline mr-1" />
                    This is a Pro feature. <button onClick={() => navigate('/pricing')} className="text-blue-600 hover:underline">Upgrade now</button>
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Prediction Results */}
            <Card className="shadow-lg border-0">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-emerald-500" />
                  Prediction Results
                </CardTitle>
              </CardHeader>
              <CardContent>
                {prediction?.prediction ? (
                  <div className="space-y-4">
                    {/* Trend */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <div>
                        <p className="text-sm text-gray-500">Predicted Trend</p>
                        <p className="text-xl font-bold capitalize">{prediction.prediction.trend}</p>
                      </div>
                      {getTrendIcon(prediction.prediction.trend)}
                    </div>

                    {/* Predicted Range */}
                    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                        Estimated Range (next {prediction.prediction.hours_ahead || 2} hours)
                      </p>
                      <p className="text-2xl font-bold text-blue-700 dark:text-blue-400">
                        {prediction.prediction.predicted_range?.[0]} - {prediction.prediction.predicted_range?.[1]} mg/dL
                      </p>
                    </div>

                    {/* Risk Level */}
                    <div className={`p-4 rounded-lg border-2 ${getRiskColor(prediction.prediction.risk_level)}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold">Risk Level</p>
                          <p className="text-sm capitalize">{prediction.prediction.risk_level}</p>
                        </div>
                        <Badge className={getRiskColor(prediction.prediction.risk_level)}>
                          {prediction.prediction.confidence} confidence
                        </Badge>
                      </div>
                    </div>

                    {/* Factors */}
                    {prediction.prediction.factors && (
                      <div>
                        <p className="text-sm font-medium text-gray-600 mb-2">Contributing Factors</p>
                        <div className="flex flex-wrap gap-2">
                          {prediction.prediction.factors.map((factor, i) => (
                            <Badge key={i} variant="outline" className="text-xs">
                              {factor}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Recommendation */}
                    {prediction.prediction.recommendation && (
                      <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200">
                        <p className="font-semibold text-emerald-800 dark:text-emerald-300 mb-1">
                          💡 Recommendation
                        </p>
                        <p className="text-sm text-emerald-700 dark:text-emerald-400">
                          {prediction.prediction.recommendation}
                        </p>
                      </div>
                    )}

                    {/* Data Points Info */}
                    <p className="text-xs text-gray-500 text-center">
                      Based on {prediction.data_points_used} glucose readings • 
                      Current avg: {prediction.current_average} mg/dL
                    </p>
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <Activity className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p>Click &quot;Generate Prediction&quot; to see your glucose forecast</p>
                    <p className="text-sm mt-2">Make sure you have at least 3 glucose readings logged</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Upgrade Dialog */}
      <Dialog open={upgradeDialogOpen} onOpenChange={setUpgradeDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown className="w-5 h-5 text-yellow-500" />
              Upgrade to Pro
            </DialogTitle>
            <DialogDescription>
              Unlock AI-powered features to better manage your diabetes
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-gradient-to-r from-violet-50 to-purple-50 rounded-lg">
              <h4 className="font-semibold text-violet-800 mb-2">Pro Features Include:</h4>
              <ul className="space-y-2 text-sm text-violet-700">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  Meal Photo AI Analysis
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  Blood Sugar Predictor
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  Unlimited Recipe Access
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  Data Export (CSV/PDF)
                </li>
              </ul>
            </div>
            <Button 
              onClick={() => {
                setUpgradeDialogOpen(false);
                navigate('/pricing');
              }}
              className="w-full bg-gradient-to-r from-violet-600 to-purple-600"
            >
              View Pricing Plans
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AIFeatures;
